import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { PerformanceType, RecommedationProfile } from '@shared/models/performance';
import { PortfolioPerformance } from '@webapi/MIF.Subscription.WebApi';
import { PerformanceProductType, PortfolioService } from '@webapi/services/portfolio.service';
import { SubscriptionHandler } from '../subscriptionHandler';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { isPeri } from '@shared/extensions/extensions';
import { constants } from '@shared/constants/constants';

@Component({
  standalone: false,
  selector: 'app-view-performance',
  templateUrl: './view-performance.component.html',
  styleUrls: ['./view-performance.component.scss']
})
export class ViewPerformanceComponent implements OnInit, OnChanges, OnDestroy {
  @Input() productType: PerformanceProductType;
  @Input() isQuestion12AnsweredYes: boolean;
  @Input() managementTypeCode: PerformanceType;
  @Input() profileTypeCode: number;
  @Input() performanceType: PerformanceType;
  @Input() onTabChanged: EventEmitter<string> = new EventEmitter();

  portfolioPerformances: PortfolioPerformance[];
  year: PortfolioPerformance;
  range: PortfolioPerformance;
  subscriptionHandler: SubscriptionHandler = new SubscriptionHandler();
  isPeri: boolean = isPeri();
  profileToUseForFiltering: string;

  constructor(public portfolioService: PortfolioService, private labelTextPipe: LabelTextPipe) {}

  async ngOnInit() {
    await this.fetchInitData();
    this.restoreData();
    this.subscriptionHandler.subscriptions = this.onTabChanged.subscribe((type: string) => {
      this.restoreData();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.profileTypeCode) {
      this.restoreData();
    }
  }

  async fetchInitData(): Promise<void> {
    this.portfolioPerformances = await this.portfolioService.getPortfolioPerformances();
  }

  restoreData(): void {
    if (!this.productType || !this.managementTypeCode || !this.profileTypeCode || !this.portfolioPerformances) {
      return;
    }

    this.profileToUseForFiltering = this.getProfileToUseForFilteringId(this.profileTypeCode);

    // Durable case processing - it means answer #12 is yes
    if (this.managementTypeCode === PerformanceType.GL && this.isQuestion12AnsweredYes === true) {
      this.profileToUseForFiltering = this.getDurableProfileToUseForFilteringId(this.profileTypeCode);
    }

    const indexOfYear = this.portfolioPerformances?.findIndex((item: PortfolioPerformance) => {
      return item.managementTypeId === this.managementTypeCode && item.profileTypeId === +this.profileToUseForFiltering && item.productTypeId === this.productType && !!item.year;
    });

    const indexOfRange = this.portfolioPerformances?.findIndex((item: PortfolioPerformance) => {
      return item.managementTypeId === this.managementTypeCode && item.profileTypeId === +this.profileToUseForFiltering && item.productTypeId === this.productType && !!item.range;
    });

    if (indexOfYear !== -1) {
      this.year = this.portfolioPerformances[indexOfYear];
    } else {
      this.year = null;
    }

    if (indexOfRange !== -1) {
      this.range = this.portfolioPerformances[indexOfRange];
    } else {
      this.range = null;
    }
  }

  getProfileToUseForFilteringId(profileTypeCode: number): string {
    switch (profileTypeCode) {
      case constants.prudent.id: {
        return RecommedationProfile.Prudent.toString();
      }
      case constants.modere.id: {
        return RecommedationProfile.Modere.toString();
      }
      case constants.equilibré.id: {
        return RecommedationProfile.Balanced.toString();
      }
      case constants.dynamiqueA.id: {
        return RecommedationProfile.Dynamic.toString();
      }
      case constants.dynamiqueR.id: {
        return RecommedationProfile.Dynamic.toString();
      }
      case constants.offensif.id: {
        return RecommedationProfile.Offensive.toString();
      }
      default: {
        return profileTypeCode.toString();
      }
    }
  }

  getDurableProfileToUseForFilteringId(profileTypeCode: number): string {
    switch (profileTypeCode) {
      case constants.prudent.id: {
        return RecommedationProfile.PrudentDurable.toString();
      }
      case constants.modere.id: {
        return RecommedationProfile.ModereDurable.toString();
      }
      case constants.equilibré.id: {
        return RecommedationProfile.BalancedDurable.toString();
      }
      case constants.dynamiqueA.id: {
        return RecommedationProfile.DynamicDurable.toString();
      }
      case constants.dynamiqueR.id: {
        return RecommedationProfile.DynamicDurable.toString();
      }
      case constants.offensif.id: {
        return RecommedationProfile.OffensiveDurable.toString();
      }
      default: {
        return profileTypeCode.toString();
      }
    }
  }

  get isPerformanceFractionWithDecimals(): boolean {
    if (!this.year?.value || !this.range?.value) {
      return false;
    }

    const isYearValueHasDecimal = this.year.value % 1 !== 0;
    const isRangeValueHasDecimal = this.range.value % 1 !== 0;

    return isYearValueHasDecimal || isRangeValueHasDecimal;
  }

  get decimalsCount(): number {
    if (!this.year?.value || !this.range?.value) {
      return 0;
    }

    const yearsValueDecimalCount = (this.year.value + '').split('.')?.[1]?.length ?? 0;
    const rangeValueDecimalCount = (this.range.value + '').split('.')?.[1]?.length ?? 0;

    return yearsValueDecimalCount > rangeValueDecimalCount ? yearsValueDecimalCount : rangeValueDecimalCount;
  }

  get tooltipText(): string {
    let text = '';

    if (!this.managementTypeCode || !this.profileTypeCode || !this.portfolioPerformances) {
      return text;
    } else {
      switch (this.managementTypeCode) {
        case PerformanceType.GL: {
          text = this.labelTextPipe.transform('Recommendation.PerformanceTypeGLTooltip');
          break;
        }
        case PerformanceType.GSM: {
          text = this.labelTextPipe.transform('Recommendation.PerformanceTypeGSMTooltip');
          break;
        }
        case PerformanceType.GH: {
          text = this.labelTextPipe.transform('Recommendation.PerformanceTypeGHTooltip');
          break;
        }
      }
    }

    return text;
  }

  ngOnDestroy(): void {
    this.subscriptionHandler.unsubscribeAll();
  }
}
